import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';

import type { TabItem } from './types';

import Skeleton from 'ui/shared/chakra/Skeleton';
import IconSvg from 'ui/shared/IconSvg';

import TabCounter from './TabCounter';

function renderTabTitle(tab: TabItem): React.ReactNode {
  return typeof tab.title === 'function' ? tab.title() : tab.title;
}

interface Props {
  tabs: Array<TabItem>;
  activeTabIndex: number;
  onItemClick: (index: number) => void;
  isLoading?: boolean;
}

const AdaptiveTabsMobile = ({ tabs, activeTabIndex, onItemClick, isLoading }: Props) => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const activeTab = tabs[activeTabIndex];

  const btnBg = useColorModeValue('#FFFFFF', '#171717');
  const btnBorder = useColorModeValue('#E9ECEF', '#222222');
  const primaryText = useColorModeValue('#000000', '#FFFFFF');
  const secondaryText = useColorModeValue('#616161', '#999999');
  const drawerBg = useColorModeValue('#FFFFFF', '#111111');
  const titleColor = useColorModeValue('#000000', '#FFFFFF');
  const handleColor = useColorModeValue('#CCCCCC', '#3F3F3F');
  const skeletonBgColor = useColorModeValue('white', 'black');
  const activeRowBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.50');

  const handleTriggerClick = React.useCallback(() => {
    if (isLoading && !isOpen) {
      return;
    }

    onToggle();
  }, [ isLoading, isOpen, onToggle ]);

  const handleTabRowClick = React.useCallback((index: number) => () => {
    onItemClick(index);
    onClose();
  }, [ onItemClick, onClose ]);

  return (
    <>
      <Box position="relative" width="100%">
        <Button
          h="40px"
          w="100%"
          px="8px"
          pl="16px"
          pr="12px"
          variant="unstyled"
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          bg={ btnBg }
          border="1px solid"
          borderColor={ btnBorder }
          borderRadius="8px"
          onClick={ handleTriggerClick }
          isActive={ isOpen }
          aria-label="Tab select"
          _hover={{ borderColor: btnBorder }}
          _active={{ borderColor: btnBorder }}
        >
          <Flex align="center" minW={ 0 } flex={ 1 } mr="8px" overflow="hidden">
            <Box
              as="span"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
              fontWeight={ 500 }
              fontSize="14px"
              color={ primaryText }
            >
              { activeTab && renderTabTitle(activeTab) }
              { activeTab && <TabCounter count={ activeTab.count }/> }
            </Box>
          </Flex>
          <IconSvg
            name="arrows/east-mini"
            transform={ isOpen ? 'rotate(-90deg)' : 'rotate(90deg)' }
            transitionDuration="faster"
            boxSize={ 3 }
            flexShrink={ 0 }
            color={ secondaryText }
          />
        </Button>
        { isLoading && !isOpen && (
          <Skeleton h="100%" w="100%" position="absolute" top={ 0 } left={ 0 } bgColor={ skeletonBgColor } borderRadius="base"/>
        ) }
      </Box>

      <Drawer isOpen={ isOpen } onClose={ onClose } placement="bottom">
        <DrawerOverlay/>
        <DrawerContent
          bg={ drawerBg }
          borderTopRadius="16px"
          minH="50vh"
          maxH="60vh"
          containerProps={{ zIndex: 1400 }}
        >
          <Box display="flex" justifyContent="center" pt="12px" pb="4px">
            <Box w="40px" h="4px" borderRadius="2px" bg={ handleColor }/>
          </Box>

          <Box px="16px" py="12px">
            <Text
              fontSize="16px"
              fontWeight={ 500 }
              fontFamily="'Space Grotesk', sans-serif"
              color={ titleColor }
            >
              Tabs
            </Text>
          </Box>

          <DrawerBody px="16px" pb="48px" overflowY="auto">
            <Flex direction="column" gap={ 1 } align="stretch">
              { tabs.map((tab, index) => {
                const rowActive = index === activeTabIndex;

                return (
                  <Button
                    key={ Array.isArray(tab.id) ? tab.id.join('-') : String(tab.id) }
                    variant="ghost"
                    justifyContent="flex-start"
                    h="auto"
                    minH="44px"
                    py={ 3 }
                    px={ 4 }
                    bg={ rowActive ? activeRowBg : undefined }
                    borderRadius="md"
                    onClick={ handleTabRowClick(index) }
                    fontWeight={ 500 }
                    fontSize="14px"
                    fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
                    color={ primaryText }
                  >
                    <Flex align="center" w="100%" gap={ 2 }>
                      <Box flex={ 1 } textAlign="left" overflow="hidden">
                        { renderTabTitle(tab) }
                        <TabCounter count={ tab.count }/>
                      </Box>
                    </Flex>
                  </Button>
                );
              }) }
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default React.memo(AdaptiveTabsMobile);
